import { useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import type { ChecklistSector } from "../../Objects/Checklist/ChecklistSector"
import type { SaveChecklistSector } from "../../Objects/Checklist/SaveChecklistSector"
import type { ChecklistTemplate } from "../../Objects/Checklist/ChecklistTemplate"
import type { SaveChecklistTemplate } from "../../Objects/Checklist/SaveChecklistTemplate"
import type { ChecklistExecution } from "../../Objects/Checklist/ChecklistExecution"
import type { ChecklistExecutionSummary } from "../../Objects/Checklist/ChecklistExecutionSummary"
import type { ChecklistPendingSummary } from "../../Objects/Checklist/ChecklistPendingSummary"
import type { SaveChecklistExecutionStep } from "../../Objects/Checklist/SaveChecklistExecutionStep"
import { DeleteRequest, DownloadFileRequest, GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"

export const useChecklist = () => {
  const { t } = useTranslation("general")

  const { setError } = useError()

  const [isLoading, setIsLoading] = useState(false)

  const run = async <T>(operation: () => Promise<T>) => {
    setIsLoading(true)

    try {
      return await operation()
    } catch (requestError) {
      const message =
        requestError instanceof Error && requestError.message.trim()
          ? requestError.message
          : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  const getMyChecklists = () =>
    run(async () => {
      const response = await GetRequest<ChecklistPendingSummary>(API_ROUTES.CHECKLIST.ME)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const startExecution = (templateId: number) =>
    run(async () => {
      const response = await PostRequest<ChecklistExecution>(API_ROUTES.CHECKLIST.ME_START_EXECUTION(templateId), {})

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getExecution = (id: number) =>
    run(async () => {
      const response = await GetRequest<ChecklistExecution>(API_ROUTES.CHECKLIST.ME_EXECUTION(id))

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const updateExecutionStep = (id: number, stepId: number, request: SaveChecklistExecutionStep) =>
    run(async () => {
      const response = await PutRequest<ChecklistExecution, SaveChecklistExecutionStep>(
        API_ROUTES.CHECKLIST.ME_UPDATE_STEP(id, stepId),
        request,
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const completeExecution = (id: number) =>
    run(async () => {
      const response = await PostRequest<ChecklistExecution>(API_ROUTES.CHECKLIST.ME_COMPLETE_EXECUTION(id), {})

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getSectors = () =>
    run(async () => {
      const response = await GetRequest<ChecklistSector[]>(API_ROUTES.CHECKLIST.SECTORS)

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const saveSector = (request: SaveChecklistSector) =>
    run(async () => {
      const response = await PostRequest<ChecklistSector>(API_ROUTES.CHECKLIST.SECTORS, request)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const deleteSector = (id: number) =>
    run(async () => {
      const response = await DeleteRequest<boolean>(API_ROUTES.CHECKLIST.SECTOR(id))

      if (!response.success) throw new Error(response.message)

      return true
    })

  const getTemplates = (includeInactive?: boolean) =>
    run(async () => {
      const response = await GetRequest<ChecklistTemplate[]>(API_ROUTES.CHECKLIST.TEMPLATES(includeInactive))

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const getTemplate = (id: number) =>
    run(async () => {
      const response = await GetRequest<ChecklistTemplate>(API_ROUTES.CHECKLIST.TEMPLATE(id))

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const saveTemplate = (request: SaveChecklistTemplate) =>
    run(async () => {
      const response = await PostRequest<ChecklistTemplate>(API_ROUTES.CHECKLIST.TEMPLATES(), request)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const duplicateTemplate = (id: number) =>
    run(async () => {
      const response = await PostRequest<ChecklistTemplate>(API_ROUTES.CHECKLIST.TEMPLATE_DUPLICATE(id), {})

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const activateTemplate = (id: number) =>
    run(async () => {
      const response = await PostRequest<ChecklistTemplate>(API_ROUTES.CHECKLIST.TEMPLATE_ACTIVATE(id), {})

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const deactivateTemplate = (id: number) =>
    run(async () => {
      const response = await PostRequest<ChecklistTemplate>(API_ROUTES.CHECKLIST.TEMPLATE_DEACTIVATE(id), {})

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const deleteTemplate = (id: number) =>
    run(async () => {
      const response = await DeleteRequest<boolean>(API_ROUTES.CHECKLIST.TEMPLATE(id))

      if (!response.success) throw new Error(response.message)

      return true
    })

  const getManagementExecution = (id: number) =>
    run(async () => {
      const response = await GetRequest<ChecklistExecution>(API_ROUTES.CHECKLIST.EXECUTION(id))

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getHistory = (
    startDate: string,
    endDate: string,
    templateId?: number,
    sectorId?: number,
    employeeId?: string,
  ) =>
    run(async () => {
      const response = await GetRequest<ChecklistExecutionSummary[]>(
        API_ROUTES.CHECKLIST.HISTORY(startDate, endDate, templateId, sectorId, employeeId),
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const downloadHistoryPdf = (startDate: string, endDate: string, templateId?: number, sectorId?: number) =>
    run(async () => {
      await DownloadFileRequest(
        API_ROUTES.CHECKLIST.HISTORY_PDF(startDate, endDate, templateId, sectorId),
        `historico-de-checklists-${startDate}-a-${endDate}.pdf`,
      )

      return true
    })

  const downloadExecutionPdf = (id: number) =>
    run(async () => {
      await DownloadFileRequest(API_ROUTES.CHECKLIST.EXECUTION_PDF(id), `checklist-${id}.pdf`)

      return true
    })

  return {
    isLoading,
    getMyChecklists,
    startExecution,
    getExecution,
    updateExecutionStep,
    completeExecution,
    getSectors,
    saveSector,
    deleteSector,
    getTemplates,
    getTemplate,
    saveTemplate,
    duplicateTemplate,
    activateTemplate,
    deactivateTemplate,
    deleteTemplate,
    getManagementExecution,
    getHistory,
    downloadHistoryPdf,
    downloadExecutionPdf,
  }
}
